import React, { useMemo, useState } from 'react';
import { Search, X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import TokenImage from './TokenImage';
import { useLanguage } from '../i18n/LanguageContext';

const POPULAR = ['BTC', 'ETH', 'USDT', 'USDC', 'SOL', 'XMR', 'BNB', 'LTC'];

/**
 * A searchable "choose a coin" modal. Two modes:
 *  - browsing: popular picks + a list of networks to drill into
 *  - searching: a flat, filtered list of coin/network matches
 * `assets.chains` is { networkName: [SYMBOL, ...] } from GET /api/assets.
 */
const AssetPicker = ({ assets, selected, onSelect, onClose, title }) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [openChain, setOpenChain] = useState(null);

  const chains = useMemo(() => {
    if (!assets?.chains) return [];
    return Object.entries(assets.chains).map(([name, symbols]) => ({ name, symbols: symbols || [] }));
  }, [assets]);

  const flat = useMemo(() => {
    const rows = [];
    chains.forEach((c) => c.symbols.forEach((s) => rows.push({ symbol: s, chain: c.name })));
    return rows;
  }, [chains]);

  const popularAvailable = useMemo(
    () => POPULAR.filter((sym) => flat.some((r) => r.symbol === sym)),
    [flat]
  );

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    return flat.filter((r) => r.symbol.toLowerCase().includes(q) || r.chain.toLowerCase().includes(q)).slice(0, 60);
  }, [flat, query]);

  const currentChain = chains.find((c) => c.name === openChain);

  const pick = (symbol) => onSelect(symbol);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          {openChain && !query && (
            <button className="modal-back" onClick={() => setOpenChain(null)} aria-label={t('picker.backAria')}>
              <ChevronLeft size={18} />
            </button>
          )}
          <h3>{openChain && !query ? openChain : title || t('picker.defaultTitle')}</h3>
          <button className="modal-close" onClick={onClose} aria-label={t('picker.closeAria')}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-search">
          <Search size={16} color="var(--ink-400)" />
          <input
            autoFocus
            type="text"
            placeholder={t('picker.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="modal-close" style={{ width: 24, height: 24 }} onClick={() => setQuery('')} aria-label={t('picker.clearSearchAria')}>
              <X size={14} />
            </button>
          )}
        </div>

        <div className="modal-body">
          {query ? (
            searchResults.length === 0 ? (
              <p className="list-empty">{t('picker.noMatch', { query })}</p>
            ) : (
              searchResults.map((r) => (
                <button
                  key={`${r.chain}-${r.symbol}`}
                  className={`list-item ${selected === r.symbol ? 'selected' : ''}`}
                  onClick={() => pick(r.symbol)}
                >
                  <span className="list-item-main">
                    <TokenImage symbol={r.symbol} chain={r.chain} size={36} />
                    <span>
                      <span className="list-item-name">{r.symbol}</span>
                      <br />
                      <span className="list-item-sub">{r.chain}</span>
                    </span>
                  </span>
                  {selected === r.symbol && <Check size={18} className="list-item-check" />}
                </button>
              ))
            )
          ) : !openChain ? (
            <>
              {popularAvailable.length > 0 && (
                <>
                  <div className="modal-section-label">{t('picker.popular')}</div>
                  <div className="chip-row">
                    {popularAvailable.map((sym) => (
                      <button key={sym} className="chip" onClick={() => pick(sym)}>
                        <TokenImage symbol={sym} size={18} />
                        {sym}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="modal-section-label">{t('picker.browseByNetwork')}</div>
              {chains.length === 0 ? (
                <p className="list-empty">{t('picker.noNetworks')}</p>
              ) : (
                chains.map((c) => (
                  <button key={c.name} className="list-item" onClick={() => setOpenChain(c.name)}>
                    <span className="list-item-main">
                      <TokenImage symbol={c.name} chain={c.name} type="chain" size={36} />
                      <span>
                        <span className="list-item-name">{c.name}</span>
                        <br />
                        <span className="list-item-sub">{t('picker.coinsCount', { count: c.symbols.length })}</span>
                      </span>
                    </span>
                    <ChevronRight size={18} color="var(--ink-400)" />
                  </button>
                ))
              )}
            </>
          ) : !currentChain || currentChain.symbols.length === 0 ? (
            <p className="list-empty">{t('picker.noCoinsOnNetwork')}</p>
          ) : (
            currentChain.symbols.map((symbol) => (
              <button
                key={symbol}
                className={`list-item ${selected === symbol ? 'selected' : ''}`}
                onClick={() => pick(symbol)}
              >
                <span className="list-item-main">
                  <TokenImage symbol={symbol} chain={currentChain.name} size={36} />
                  <span className="list-item-name">{symbol}</span>
                </span>
                {selected === symbol && <Check size={18} className="list-item-check" />}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetPicker;
